import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Eye, Printer } from 'lucide-react';
import { contratsApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import { useApp } from '../../context/AppContext';
import { getStatutBadgeClass, getStatutLabel, formatMontant } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function ContratsList() {
  const { getApiParams, structures } = useApp();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterStructure, setFilterStructure] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      ...getApiParams(),
      ...(filterStatut && { statut: filterStatut }),
      ...(filterStructure && { structure_id: filterStructure }),
    };
    contratsApi.getAll(params)
      .then(res => setContrats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams, filterStatut, filterStructure]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  const handleStatut = async (id, statut) => {
    try {
      await contratsApi.updateStatut(id, statut);
      load();
    } catch (err) {
      alert('Erreur');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="form-select w-40 text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="actif">Actif</option>
            <option value="termine">Terminé</option>
            <option value="suspendu">Suspendu</option>
          </select>
          <select
            value={filterStructure}
            onChange={e => setFilterStructure(e.target.value)}
            className="form-select w-52 text-sm"
          >
            <option value="">Toutes les structures</option>
            {structures.map(s => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </div>
        <Link to="/devis/nouveau" className="btn-primary">
          <FileSignature size={16} /> Via nouveau devis
        </Link>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement...</div>
        ) : contrats.length === 0 ? (
          <div className="py-16 text-center">
            <FileSignature size={40} className="text-gray-300 mx-auto mb-3" />
            <div className="text-gray-400 text-sm">Aucun contrat trouvé</div>
            <p className="text-xs text-gray-400 mt-2">
              Les contrats sont créés depuis les devis acceptés.
            </p>
            <Link to="/devis" className="btn-secondary mt-4 inline-flex">Voir les devis</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">N°</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Début</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Fin</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">H/sem</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Mensualité</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contrats.map((c, i) => (
                  <tr key={c.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">#{String(c.id).padStart(4, '0')}</td>
                    <td className="px-4 py-3">
                      {c.enfant_id ? (
                        <Link to={`/enfants/${c.enfant_id}`} className="font-medium text-gray-900 hover:text-primary-500">
                          {c.enfant_prenom} {c.enfant_nom}
                        </Link>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{c.structure_nom}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.date_debut)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.date_fin)}</td>
                    <td className="px-4 py-3 text-gray-700">{(c.heures_hebdo || 0).toFixed(1)}h</td>
                    <td className="px-4 py-3 font-semibold text-primary-600">{formatMontant(c.montant_mensuel)}</td>
                    <td className="px-4 py-3">
                      <span className={getStatutBadgeClass(c.statut)}>{getStatutLabel(c.statut)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {c.statut === 'actif' && (
                          <button
                            onClick={() => handleStatut(c.id, 'termine')}
                            className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                          >
                            Terminer
                          </button>
                        )}
                        <a href={contratsApi.getPdfUrl(c.id)} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-gold-50 text-gray-500 hover:text-gold-600" title="Imprimer / PDF du contrat">
                          <Printer size={16} />
                        </a>
                        <SignatureButton type="contrat" refId={c.id} nom={`${c.enfant_prenom || ''}`} compact />
                        {c.devis_id && (
                          <Link to={`/devis/${c.devis_id}`} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary-500" title="Voir devis">
                            <Eye size={16} />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && (
        <div className="text-xs text-gray-400">{contrats.length} contrat{contrats.length > 1 ? 's' : ''}</div>
      )}
    </div>
  );
}
